import { useCallback, useEffect, useRef, useState } from 'react';
import { driver, Driver } from 'driver.js';
import { isWeb } from '@helpers';
import { DEFAULT_DRIVER_CONFIG } from '@config/driver';
import { buildDriverSteps } from '@components/tours/helpers';
import type { TourConfig } from '@components/tours/types';

export const useTourGuide = () => {
  const driverRef = useRef<Driver | null>(null);
  const wasClosedByUserRef = useRef(false);
  const [isActive, setIsActive] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);

  // Cleanup driver instance on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  const startTour = useCallback((tourConfig: TourConfig, onTourDestroyed?: (closedByUser: boolean) => void) => {
    if (!isWeb) {
      return;
    }

    if (driverRef.current) {
      driverRef.current.destroy();
    }

    const driverSteps = buildDriverSteps(tourConfig.steps);

    driverRef.current = driver({
      ...DEFAULT_DRIVER_CONFIG,
      ...tourConfig.driverConfig,
      steps: driverSteps,
      onCloseClick: (_el: any, _step: any, opts: { driver: Driver }) => {
        wasClosedByUserRef.current = true;
        opts.driver.destroy();
      },
      overlayClickBehavior: (_el: any, _step: any, opts: { driver: Driver }) => {
        wasClosedByUserRef.current = true;
        opts.driver.destroy();
      },
      onDestroyed: () => {
        const wasClosedByUser = wasClosedByUserRef.current;
        wasClosedByUserRef.current = false;
        setIsActive(false);
        setCurrentTourId(null);
        driverRef.current = null;
        onTourDestroyed?.(wasClosedByUser);
      },
    });

    setIsActive(true);
    setCurrentTourId(tourConfig.id);
    driverRef.current.drive();
  }, []);

  const stopTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    setIsActive(false);
    setCurrentTourId(null);
  }, []);

  return {
    startTour,
    stopTour,
    isActive,
    currentTourId,
  };
};
